import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Switch,
  TextField,
  CircularProgress,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import AlertModal from '../components/AlertModal';
import OperationalHoursEditor from '../components/OperationalHoursEditor';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import { updateBranchStatus, updateBranchOffer, getBranchById } from '../services/branchService';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const {
    branches,
    restaurantName,
    selectedBranch,
    listenAllMode,
    setListenAllMode,
    restaurants,
    allBranches,
    isLoading: branchLoading,
    requestBranchSelect,
    switchBranch,
  } = useBranch();

  const [branchOpen, setBranchOpen] = useState(false);
  const [offerValue, setOfferValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [offerSaving, setOfferSaving] = useState(false);
  const [feedback, setFeedback] = useState({ visible: false, type: '', title: '', message: '' });
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    if (restaurants?.length > 1 || allBranches?.length > 1) {
      requestBranchSelect();
    }
  }, [restaurants?.length, allBranches?.length, requestBranchSelect]);

  const showAlert = (title, message) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertVisible(true);
  };

  const showFeedback = (type, title, message) => {
    setFeedback({ visible: true, type, title, message });
    setTimeout(() => setFeedback({ visible: false, type: '', title: '', message: '' }), 2000);
  };

  useEffect(() => {
    if (selectedBranch) {
      setBranchOpen(!!selectedBranch?.isOperational);
      const existingOffer = selectedBranch?.offer ?? selectedBranch?.restaurant?.offer;
      if (existingOffer !== undefined && existingOffer !== null) {
        setOfferValue(String(existingOffer));
      }
    }
  }, [selectedBranch?._id, selectedBranch?.isOperational, selectedBranch?.offer]);

  const hydrateBranchDetails = useCallback(async () => {
    if (!selectedBranch?._id) return;
    try {
      const detail = await getBranchById(selectedBranch._id);
      const b = detail?.branch || detail;
      if (b) {
        setBranchOpen(!!b.isOperational);
        const existingOffer = b.offer ?? b.restaurant?.offer;
        if (existingOffer !== undefined && existingOffer !== null) {
          setOfferValue(String(existingOffer));
        }
      }
    } catch (error) {
      
    }
  }, [selectedBranch?._id]);

  const handleBranchStatusToggle = async () => {
    if (!selectedBranch?._id) return;
    setLoading(true);
    try {
      const next = !branchOpen;
      setBranchOpen(next);
      await updateBranchStatus(selectedBranch._id, next);
      await hydrateBranchDetails();
      showFeedback('success', 'Success', `Branch is now ${next ? 'Open' : 'Closed'}`);
    } catch (error) {
      setBranchOpen(!branchOpen);
      showFeedback('error', 'Error', 'Failed to update branch status');
    } finally {
      setLoading(false);
    }
  };

  const handleOfferSave = async () => {
    if (!selectedBranch?._id) {
      showAlert('Error', 'No branch selected');
      return;
    }
    setOfferSaving(true);
    try {
      const percent = Math.max(0, Math.min(100, Number(offerValue)));
      await updateBranchOffer(selectedBranch._id, percent);
      await hydrateBranchDetails();
      showFeedback('success', 'Success', 'Branch offer updated');
    } catch (error) {
      showFeedback('error', 'Error', 'Failed to update branch offer');
    } finally {
      setOfferSaving(false);
    }
  };

  const handleListenAllToggle = async () => {
    try {
      const nextMode = !listenAllMode;
      await setListenAllMode(nextMode);
      showFeedback('success', 'Success', `Listen-all mode ${nextMode ? 'enabled' : 'disabled'}`);
    } catch (error) {
      showFeedback('error', 'Error', 'Failed to update listen-all mode');
    }
  };

  const handleBranchChange = (branchId) => {
    const branch = allBranches.find(b => b._id === branchId);
    if (branch) {
      switchBranch(branch);
    }
  };

  if (!selectedBranch && !listenAllMode) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
        <AppHeader title="Settings" showBack onBack={() => navigate(-1)} />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography>No branch selected</Typography>
          {branchLoading && <CircularProgress sx={{ mt: 2, color: '#851E41' }} />}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <AppHeader title="Settings" showBack onBack={() => navigate(-1)} />
      <AlertModal
        visible={alertVisible}
        title={alertTitle}
        message={alertMessage}
        onRequestClose={() => setAlertVisible(false)}
      />

      <Box sx={{ p: 2 }}>
        {branchLoading && restaurants.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={20} sx={{ color: '#851E41' }} />
            <Typography sx={{ color: '#6B7280' }}>Loading settings...</Typography>
          </Box>
        ) : null}

        {!branchLoading && restaurants && restaurants.length > 1 && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1F2937', mb: 2 }}>
                Multi-Restaurant Settings
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#1F2937', mb: 0.5 }}>
                    Receive orders from all outlets
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                    Receive orders from all your restaurants and branches simultaneously.
                  </Typography>
                </Box>
                <Switch
                  checked={listenAllMode}
                  onChange={handleListenAllToggle}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#22c55e',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#22c55e',
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        )}

        {allBranches && allBranches.length > 1 && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1F2937', mb: 2 }}>
                Select Branch
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#6B7280', mb: 1.5 }}>
                Choose which branch you want to configure settings for.
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedBranch?._id || ''}
                  onChange={(e) => handleBranchChange(e.target.value)}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      py: 1.2,
                    },
                  }}
                >
                  {allBranches.map(branch => (
                    <MenuItem key={branch._id} value={branch._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Typography>{branch.name}</Typography>
                        <Box
                          component="span"
                          sx={{
                            fontSize: 12,
                            color: branch?.isOperational ? '#10B981' : '#EF4444',
                            ml: 1,
                          }}
                        >
                          {branch?.isOperational ? 'Open' : 'Closed'}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        )}

        <Card sx={{ mb: 2, borderRadius: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1F2937', mb: 2 }}>
              Branch Settings
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ flex: 1, mr: 2 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#1F2937', mb: 0.5 }}>
                  Branch availability
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                  Open/close this branch for orders.
                </Typography>
              </Box>
              <Switch
                checked={branchOpen}
                onChange={handleBranchStatusToggle}
                disabled={loading}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#22c55e',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#22c55e',
                  },
                  '& .MuiSwitch-switchBase.Mui-notChecked': {
                    color: '#ef4444',
                  },
                  '& .MuiSwitch-switchBase.Mui-notChecked + .MuiSwitch-track': {
                    backgroundColor: '#ef4444',
                  },
                }}
              />
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1, mr: 2 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#1F2937', mb: 1 }}>
                  Branch Offer (%)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  value={offerValue}
                  onChange={(e) => setOfferValue(e.target.value.replace(/[^0-9.]/g, ''))}
                  placeholder="0-100"
                  size="small"
                  sx={{ width: 80 }}
                  inputProps={{ style: { textAlign: 'center' } }}
                />
                <Button
                  variant="contained"
                  onClick={handleOfferSave}
                  disabled={offerSaving}
                  size="small"
                  sx={{
                    bgcolor: '#851E41',
                    '&:hover': { bgcolor: '#6B1830' },
                  }}
                >
                  {offerSaving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2, borderRadius: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1F2937', mb: 2 }}>
              Operational Hours
            </Typography>
            <OperationalHoursEditor branchId={selectedBranch?._id} onSaved={hydrateBranchDetails} />
          </CardContent>
        </Card>

        <Card sx={{ mb: 2, borderRadius: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1F2937', mb: 2 }}>
              Account
            </Typography>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 12, color: '#6B7280' }}>Restaurant</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#1F2937' }}>
                {selectedBranch?.restaurant?.name || selectedBranch?.restaurantName || restaurantName || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 12, color: '#6B7280' }}>Branch</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#1F2937' }}>
                {selectedBranch?.name || 'N/A'}
              </Typography>
            </Box>
            {user && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 12, color: '#6B7280' }}>Logged in as</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#1F2937' }}>
                  {user.name || user.phoneNumber || 'User'}
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              fullWidth
              onClick={logout}
              sx={{ bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' }, mt: 1 }}
            >
              Logout
            </Button>
          </CardContent>
        </Card>
      </Box>

      {feedback.visible && (
        <Box
          sx={{
            position: 'fixed',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: feedback.type === 'success' ? '#10B981' : '#EF4444',
            color: '#fff',
            px: 3,
            py: 2,
            borderRadius: 2,
            zIndex: 1000,
            boxShadow: 3,
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>{feedback.title}</Typography>
          <Typography sx={{ fontSize: 14 }}>{feedback.message}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default SettingsPage;
